import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux/common/store';
import { CountrySelect, StateSelect, TimezoneSelect } from '@/components/UI/GeoSelect';
import {
  useGetTenantSettingsQuery,
  useUpdateTenantMutation,
} from '@/redux/internal-services/onboardingApi';
import { StepShell } from './StepShell';
import type { PlaceCandidate, StepHandle, SubmitResult } from './stepHandle';
import { fieldClass } from '@/components/UI/fieldStyles';

interface Props {
  onBack?: () => void;
  onNext: () => void;
  disabled?: boolean;
}

// The saved company details from the tenant settings ('' for anything not saved yet).
function savedFields(data: unknown) {
  const d = (data as Record<string, unknown> | undefined) ?? {};
  const addr = (d.address as Record<string, string> | undefined) ?? {};
  return {
    companyName: (d.company_name as string) || '',
    countryCode: addr.country || '',
    street: addr.street || '',
    city: addr.city || '',
    stateCode: addr.state || '',
    zip: addr.zip || '',
    timezone: (d.timezone as string) || '',
  };
}

/**
 * Guided-setup "Company Info" step — company name + business address + timezone.
 * Prefills from the tenant record and persists via PUT /api/v1/tenants/me.
 * Exposes a {@link StepHandle} so the voice agent can read/fill fields and submit.
 */
const CompanyInfoStep = forwardRef<StepHandle, Props>(({ onBack, onNext, disabled = false }, ref) => {
  const { t } = useTranslation();
  const { data: tenantSettings } = useGetTenantSettingsQuery();
  const [updateTenant, { isLoading: saving }] = useUpdateTenantMutation();

  // Seeded from the cached tenant settings so the very first render (and the voice agent's first getState())
  // already has the saved values, not blanks that an effect fills a moment later.
  const saved = savedFields(tenantSettings?.data);
  const [companyName, setCompanyName] = useState(saved.companyName);
  // Default to the United States (the only seeded country) so the field is pre-filled.
  const [countryCode, setCountryCode] = useState(saved.countryCode || 'US');
  const [street, setStreet] = useState(saved.street);
  const [city, setCity] = useState(saved.city);
  const [stateCode, setStateCode] = useState(saved.stateCode);
  const [zip, setZip] = useState(saved.zip);
  const [timezone, setTimezone] = useState(saved.timezone);
  const [error, setError] = useState('');
  // Tappable disambiguation options from the voice agent's resolve_place tool — cleared once
  // a fresh (unambiguous) lookup runs, a candidate is chosen, or the user fills the form by hand.
  const [candidates, setCandidates] = useState<PlaceCandidate[]>([]);

  // The place options come from the voice agent, so clear them the moment voice stops.
  const voiceStatus = useSelector((s: RootState) => s.onboardingVoice.voiceStatus);
  const isVoiceActive = voiceStatus !== 'idle' && voiceStatus !== 'error';
  useEffect(() => {
    if (!isVoiceActive) setCandidates([]);
  }, [isVoiceActive]);

  // Synchronous mirror of the field values so the imperative handle reads fresh values even
  // when multiple voice actions land before React re-renders.
  const fieldsRef = useRef({ companyName, countryCode, street, city, stateCode, zip, timezone });
  useEffect(() => {
    fieldsRef.current = { companyName, countryCode, street, city, stateCode, zip, timezone };
  }, [companyName, countryCode, street, city, stateCode, zip, timezone]);

  useEffect(() => {
    if (!tenantSettings?.data) return;
    const f = savedFields(tenantSettings.data);
    // Only what is saved overwrites the form. The mirror is updated in the same tick: the agent reads getState()
    // right after this step opens, before the effect above has copied the new state into it.
    const next = { ...fieldsRef.current };
    if (f.companyName) { setCompanyName(f.companyName); next.companyName = f.companyName; }
    if (f.countryCode) { setCountryCode(f.countryCode); next.countryCode = f.countryCode; }
    if (f.street) { setStreet(f.street); next.street = f.street; }
    if (f.city) { setCity(f.city); next.city = f.city; }
    if (f.stateCode) { setStateCode(f.stateCode); next.stateCode = f.stateCode; }
    if (f.zip) { setZip(f.zip); next.zip = f.zip; }
    if (f.timezone) { setTimezone(f.timezone); next.timezone = f.timezone; }
    fieldsRef.current = next;
  }, [tenantSettings]);

  // Persist current values. Returns a discriminated result the page/agent can act on.
  const persist = async (): Promise<SubmitResult> => {
    const f = fieldsRef.current;
    if (!f.companyName.trim()) {
      setError(t('onboarding.steps.companyNameRequired'));
      return { ok: false, reason: 'validation', message: 'Company name is required.' };
    }
    setError('');
    try {
      await updateTenant({
        company_name: f.companyName.trim(),
        address: { street: f.street, city: f.city, state: f.stateCode, zip: f.zip, country: f.countryCode },
        timezone: f.timezone || undefined,
      }).unwrap();
      return { ok: true };
    } catch {
      setError(t('common.failedToSave'));
      return { ok: false, reason: 'transient', message: 'Failed to save company info.' };
    }
  };

  const handleNext = () => { void persist().then((r) => { if (r.ok) onNext(); }); };

  // Set a field by voice. Keeps the synchronous mirror in step with React state.
  const setFieldById = (id: string, value: string) => {
    const setters: Record<string, (v: string) => void> = {
      company_name: setCompanyName,
      country: setCountryCode,
      street: setStreet,
      city: setCity,
      state: setStateCode,
      zip: setZip,
      timezone: setTimezone,
    };
    const key: Record<string, keyof typeof fieldsRef.current> = {
      company_name: 'companyName', country: 'countryCode', street: 'street',
      city: 'city', state: 'stateCode', zip: 'zip', timezone: 'timezone',
    };
    if (setters[id]) {
      fieldsRef.current = { ...fieldsRef.current, [key[id]]: value };
      setters[id](value);
    }
  };

  // Fill the address fields from a tapped candidate. No socket call needed — the voice hook's
  // manual-edit watcher notices the diff on its next tick and tells the agent, same as a
  // hand-typed edit.
  const applyCandidate = (candidate: PlaceCandidate) => {
    if (candidate.street) setFieldById('street', candidate.street);
    if (candidate.city) setFieldById('city', candidate.city);
    if (candidate.state) setFieldById('state', candidate.state);
    if (candidate.zip) setFieldById('zip', candidate.zip);
    if (candidate.timezone) setFieldById('timezone', candidate.timezone);
    setCandidates([]);
  };

  useImperativeHandle(ref, (): StepHandle => ({
    getState: () => ({ ...fieldsRef.current }),
    submit: persist,
    setField: setFieldById,
    setCandidates,
  }));

  const inputCls = fieldClass();

  return (
    <StepShell
      title="Company Info"
      subtitle="Tell us about your company"
      onBack={onBack}
      onNext={handleNext}
      saving={saving}
      error={error}
      disabled={disabled}
    >
      <div className="mb-4">
        <label className="block text-sm font-medium text-ui-text mb-1.5">Company Name</label>
        <input
          type="text"
          placeholder="Smith Roofing Co."
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          className={inputCls}
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-ui-text mb-1.5">Country</label>
        <CountrySelect
          value={countryCode}
          onChange={(code, _name, tz) => {
            setCountryCode(code);
            if (tz && !timezone) setTimezone(tz);
          }}
          onResolve={(code) => setCountryCode(code)}
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-ui-text mb-1.5">Street Address</label>
        <input
          type="text"
          placeholder="123 Main St"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          className={inputCls}
        />
      </div>

      {/* Disambiguation options from the voice place lookup — tap one to fill the address.
          `pointer-events-auto` + full opacity override StepShell's disabled overlay so these stay
          clickable while voice is active (the rest of the form is intentionally locked then). */}
      {candidates.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2 pointer-events-auto opacity-100">
          {candidates.map((c, i) => (
            <button
              key={`${c.label}-${i}`}
              type="button"
              onClick={() => applyCandidate(c)}
              className="text-sm font-medium px-3 py-1.5 rounded-full border border-ui-border bg-ui-card hover:border-ui-ring hover:bg-ui-hover text-ui-text transition-colors"
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-sm font-medium text-ui-text mb-1.5">City</label>
          <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-ui-text mb-1.5">State</label>
          <StateSelect
            countryCode={countryCode}
            value={stateCode}
            onChange={(code, _name, tz) => {
              setStateCode(code);
              if (tz) setTimezone(tz);
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-2">
        <div>
          <label className="block text-sm font-medium text-ui-text mb-1.5">Zip</label>
          <input type="text" value={zip} onChange={(e) => setZip(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-sm font-medium text-ui-text mb-1.5">Timezone</label>
          <TimezoneSelect value={timezone} onChange={setTimezone} />
        </div>
      </div>
    </StepShell>
  );
});

CompanyInfoStep.displayName = 'CompanyInfoStep';

export default CompanyInfoStep;
